/**
 * resumeStorage.ts
 *
 * Safe, per-tab, versioned helpers for reading and writing conversation resume
 * points to sessionStorage.
 *
 * Key design decisions
 * ─────────────────────
 * 1. Per-tab keys  — each tab appends its own unique `tabId` so that two tabs
 *    opened on the same conversation never overwrite each other's resume point.
 * 2. Schema version — if the stored shape ever changes, the old data is rejected
 *    and cleaned up automatically rather than causing a runtime error.
 * 3. TTL            — entries older than 24 h are considered stale and discarded.
 * 4. Defensive I/O  — all reads are wrapped in try/catch so corrupt JSON never
 *    surfaces as an unhandled exception.
 */

const SCHEMA_VERSION = 1;
const TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const KEY_PREFIX = "chat_resume_";

// ── Public types ─────────────────────────────────────────────────────────────

export interface ResumePoint {
  /** Index in the messages array where new messages begin after history load. */
  resumedAtIndex: number;
  /** ISO-8601 timestamp recorded when the conversation was first resumed. */
  sessionStartTime: string;
}

// ── Internal types ────────────────────────────────────────────────────────────

interface StoredEntry extends ResumePoint {
  v: number;      // schema version
  storedAt: number; // epoch ms
  tabId: string;
}

// ── Key helpers ───────────────────────────────────────────────────────────────

function makeKey(convId: string, tabId: string): string {
  return `${KEY_PREFIX}${convId}_${tabId}`;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Read a validated resume point for the given conversation + tab.
 * Returns `null` if no entry exists, the entry is corrupt, has the wrong
 * schema version, or has expired.
 */
export function getResumePoint(convId: string, tabId: string): ResumePoint | null {
  const key = makeKey(convId, tabId);
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;

    const entry = JSON.parse(raw) as StoredEntry;

    const isValid =
      entry.v === SCHEMA_VERSION &&
      typeof entry.resumedAtIndex === "number" &&
      Number.isFinite(entry.resumedAtIndex) &&
      entry.resumedAtIndex >= 0 &&
      typeof entry.sessionStartTime === "string" &&
      entry.sessionStartTime.length > 0 &&
      typeof entry.storedAt === "number" &&
      Date.now() - entry.storedAt <= TTL_MS;

    if (!isValid) {
      sessionStorage.removeItem(key);
      return null;
    }

    return { resumedAtIndex: entry.resumedAtIndex, sessionStartTime: entry.sessionStartTime };
  } catch {
    // JSON.parse failed or sessionStorage is unavailable — remove the bad entry
    try { sessionStorage.removeItem(key); } catch { /* ignore */ }
    return null;
  }
}

/**
 * Persist a resume point for the given conversation + tab.
 * Silently no-ops if sessionStorage is unavailable or over quota.
 */
export function setResumePoint(convId: string, tabId: string, point: ResumePoint): void {
  const entry: StoredEntry = {
    ...point,
    v: SCHEMA_VERSION,
    storedAt: Date.now(),
    tabId,
  };
  try {
    sessionStorage.setItem(makeKey(convId, tabId), JSON.stringify(entry));
  } catch {
    // sessionStorage quota exceeded or unavailable — not critical, fail silently
  }
}

/**
 * Remove the resume point for the given conversation + tab (e.g. on clearChat).
 */
export function clearResumePoint(convId: string, tabId: string): void {
  try {
    sessionStorage.removeItem(makeKey(convId, tabId));
  } catch { /* ignore */ }
}

/**
 * Scan sessionStorage and remove all `chat_resume_*` entries that are expired,
 * corrupt, or carry an outdated schema version.
 * Call once on application mount to avoid unbounded storage growth.
 */
export function pruneExpiredResumePoints(): void {
  const keysToDelete: string[] = [];
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (!key?.startsWith(KEY_PREFIX)) continue;
      try {
        const raw = sessionStorage.getItem(key);
        if (!raw) { keysToDelete.push(key); continue; }
        const entry = JSON.parse(raw) as StoredEntry;
        if (
          entry.v !== SCHEMA_VERSION ||
          typeof entry.storedAt !== "number" ||
          Date.now() - entry.storedAt > TTL_MS
        ) {
          keysToDelete.push(key);
        }
      } catch {
        keysToDelete.push(key);
      }
    }
  } catch { /* sessionStorage unavailable */ }

  keysToDelete.forEach((k) => {
    try { sessionStorage.removeItem(k); } catch { /* ignore */ }
  });
}
